import type {
  AppConfig,
  AuthSession,
  BankAccount,
  BankAccountInfo,
  CandlesResponse,
  ClosedPosition,
  CreateAlertRequest,
  Deposit,
  DepositRequest,
  DepositResponse,
  EffectiveTrading,
  FillOutcome,
  Instrument,
  ModifyOrderRequest,
  PriceAlert,
  JournalStats,
  LedgerEntry,
  MarketStatus,
  Notification,
  NotificationCategory,
  NotificationPrefs,
  OptionChain,
  Order,
  PlaceOrderRequest,
  PnlReport,
  Position,
  Quote,
  SessionInfo,
  TradeBook,
  WalletSummary,
  WithdrawRequest,
  Withdrawal,
} from '@dabba/contracts';
import { API_URL } from './config';
import { useSession } from '../store/session';
import { getDeviceId } from './deviceId';
import { toast } from '../ui/toast';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** Parsed error body — recovery handlers may need extra fields from it. */
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/**
 * Thin typed fetch wrapper. The bearer token is read from the session store on
 * every call rather than captured once, so signing in or out takes effect on
 * the very next request.
 */
async function request<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const token = useSession.getState().token;
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) };
  if (init?.json !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;
  const deviceId = getDeviceId();
  if (deviceId) headers['x-device-id'] = deviceId;
  // The server names the device on the sessions screen, so it needs to know
  // this is a browser and not a native app — it cannot tell from a user-agent
  // alone, and one parser server-side beats each client inventing its own.
  headers['x-platform'] = 'web';

  // Hard cap on any request: a fetch that never settles would leave screens —
  // worst of all the order ticket — spinning forever with nothing to show.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15_000);
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...init,
      headers,
      body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
      signal: ctrl.signal,
    });
  } catch (e) {
    const timedOut = e instanceof Error && e.name === 'AbortError';
    throw new ApiError(
      timedOut ? 'Request timed out — check your connection' : 'Network unavailable — check your connection',
      0,
      timedOut ? 'TIMEOUT' : 'NETWORK',
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    let detail: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as { error?: string; code?: string } & Record<string, unknown>;
      if (body.error) message = body.error;
      code = body.code;
      detail = body;
    } catch {
      // non-JSON error body
    }
    if (res.status === 401 && token) {
      // Tell the user WHY they were signed out — a silent logout reads as
      // data loss. One notice per expiry, not one per failed query.
      useSession.getState().logout();
      notifySessionExpired();
    }
    throw new ApiError(message, res.status, code, detail);
  }
  // 204s carry no body; every other success on this API is JSON.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

let expiredNoticeAt = 0;
function notifySessionExpired(): void {
  const now = Date.now();
  if (now - expiredNoticeAt < 60_000) return; // a burst of parallel 401s → one notice
  expiredNoticeAt = now;
  toast({ kind: 'rejected', title: 'Session expired', detail: 'Please sign in again to continue.' });
}

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
};

export type MeResponse = AuthSession['user'] & { virtualCapital: number; payoutWallet: number };

export type PortfolioResponse = {
  dayPnl: number;
  dayPnlPct: number;
  invested: number;
  current: number;
  realizedToday: number;
  capital: number;
  availableFunds: number;
  positions: Position[];
  closedToday: ClosedPosition[];
};

export type HoldingsResponse = {
  holdings: Position[];
  invested: number;
  current: number;
  totalPnl: number;
  totalPnlPct: number;
};

export const api = {
  // ---- auth ----
  otpSend: (phone: string) => request<{ ok: true }>('/auth/otp/send', { method: 'POST', json: { phone } }),
  otpVerify: (phone: string, code: string) =>
    request<AuthSession>('/auth/otp/verify', { method: 'POST', json: { phone, code } }),
  me: () => request<MeResponse>('/me'),
  tradingConfig: () => request<EffectiveTrading>('/me/trading'),
  updateMe: (body: { name: string }) => request<AuthSession['user']>('/me', { method: 'PATCH', json: body }),
  appConfig: () => request<AppConfig>('/app/config'),

  // ---- sessions (where this account is signed in) ----
  sessions: () => request<{ sessions: SessionInfo[] }>('/me/sessions'),
  revokeSession: (id: string) =>
    request<{ ok: true }>(`/me/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  revokeOtherSessions: () =>
    request<{ ok: true; revoked: number }>('/me/sessions/revoke-others', { method: 'POST' }),
  /** Ends this device server-side; the local sign-out follows regardless. */
  logout: () => request<{ ok: true }>('/me/logout', { method: 'POST' }),

  // ---- market data ----
  marketStatus: () => request<MarketStatus>('/market/status'),
  searchInstruments: (q: string) => request<{ results: Instrument[] }>(`/instruments/search${qs({ q })}`),
  instrument: (symbol: string) => request<Instrument>(`/instruments/${encodeURIComponent(symbol)}`),
  instrumentsMeta: (symbols: string[]) =>
    request<{ results: Instrument[] }>(`/instruments/meta${qs({ symbols: symbols.join(',') })}`),
  quote: (symbol: string) => request<Quote>(`/quote/${encodeURIComponent(symbol)}`),
  candles: (symbol: string, tf: string) =>
    request<CandlesResponse>(`/candles/${encodeURIComponent(symbol)}${qs({ tf })}`),
  optionChain: (underlying: string, opts: { expiry?: string; strike?: number } = {}) =>
    request<OptionChain>(`/chain/${encodeURIComponent(underlying)}${qs(opts)}`),

  // ---- watchlist ----
  getWatchlist: () => request<{ symbols: string[] }>('/me/watchlist'),
  putWatchlist: (symbols: string[]) =>
    request<{ ok: true; symbols: string[] }>('/me/watchlist', { method: 'PUT', json: { symbols } }),

  // ---- trading ----
  placeOrder: (order: PlaceOrderRequest) =>
    request<{ order: Order; outcome: FillOutcome }>('/orders', { method: 'POST', json: order }),
  orders: () => request<Order[]>('/orders'),
  modifyOrder: (id: string, patch: ModifyOrderRequest) =>
    request<{ order: Order }>(`/orders/${id}`, { method: 'PATCH', json: patch }),
  cancelOrder: (id: string) => request<{ order: Order }>(`/orders/${id}`, { method: 'DELETE' }),
  positions: () => request<Position[]>('/positions'),
  portfolio: () => request<PortfolioResponse>('/portfolio'),
  holdings: () => request<HoldingsResponse>('/holdings'),

  // ---- wallet & funding ----
  wallet: () => request<WalletSummary>('/wallet'),
  walletLedger: (before?: string) =>
    request<{ rows: LedgerEntry[]; nextBefore: string | null }>(`/wallet/ledger${qs({ before })}`),
  getDepositBank: () =>
    request<{
      bank: {
        bankName: string;
        accountHolder: string;
        accountNumber: string;
        ifsc: string;
        upiId?: string;
      };
      estimatedHours: number;
    }>('/wallet/deposit-bank'),
  createDeposit: (body: DepositRequest) =>
    request<DepositResponse>('/wallet/deposits', { method: 'POST', json: body }),
  verifyDeposit: (body: {
    depositId: string;
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }) => request<{ status: 'paid'; depositId: string }>('/wallet/deposits/verify', { method: 'POST', json: body }),
  deposits: () => request<Deposit[]>('/wallet/deposits'),
  getBank: () => request<{ bank: BankAccountInfo | null }>('/wallet/bank'),
  putBank: (body: BankAccount) =>
    request<{ bank: BankAccountInfo }>('/wallet/bank', { method: 'PUT', json: body }),
  requestWithdrawal: (body: WithdrawRequest) =>
    request<Withdrawal>('/wallet/withdrawals', { method: 'POST', json: body }),
  withdrawals: () => request<Withdrawal[]>('/wallet/withdrawals'),

  // ---- reports ----
  journal: () => request<JournalStats>('/journal'),
  trades: (opts: { before?: string; symbol?: string; limit?: number } = {}) =>
    request<TradeBook>(`/reports/trades${qs(opts)}`),
  pnlReport: (opts: { from?: string; to?: string } = {}) =>
    request<PnlReport>(`/reports/pnl${qs(opts)}`),

  // ---- price alerts ----
  alerts: () => request<{ alerts: PriceAlert[] }>('/me/alerts'),
  createAlert: (body: CreateAlertRequest) =>
    request<{ alert: PriceAlert }>('/me/alerts', { method: 'POST', json: body }),
  deleteAlert: (id: string) => request<{ ok: true }>(`/me/alerts/${id}`, { method: 'DELETE' }),

  // ---- notifications ----
  getNotifications: (before?: string) =>
    request<{ rows: Notification[]; nextBefore: string | null; unread: number }>(
      `/me/notifications${qs({ before })}`,
    ),
  markNotificationsRead: (upTo: string) =>
    request<{ ok: true }>('/me/notifications/read', { method: 'POST', json: { upTo } }),
  clearNotifications: () => request<{ ok: true; deleted: number }>('/me/notifications', { method: 'DELETE' }),
  getNotificationPrefs: () =>
    request<{ prefs: Record<NotificationCategory, boolean> }>('/me/notification-prefs'),
  putNotificationPrefs: (prefs: NotificationPrefs) =>
    request<{ prefs: Record<NotificationCategory, boolean> }>('/me/notification-prefs', {
      method: 'PUT',
      json: prefs,
    }),
};
